/**
 * Explanation Panel Component
 * Implements PRD FR-39, FR-41, FR-42
 */

import { useSimulationStore } from '../state/simulationStore';
import { TimeStep } from '../model/types';

const EXPLANATIONS: Record<TimeStep, { title: string; content: string[] }> = {
  1: {
    title: 'The Liquidity Event',
    content: [
      'A liquidity event occurs: LT1 holds i units and needs to trade. However, the offsetting trader (LT2) won\'t arrive until t=2. This creates a temporary order imbalance.',
      'The n market makers step in to provide immediacy. They absorb the order flow, each taking i/(n+1) units.',
      'Note that LT1 also retains i/(n+1) units - optimal behavior means even the liquidity trader doesn\'t fully liquidate!',
      'The price S₁ is set below the fundamental value μ to compensate market makers for the risk of holding inventory exposed to the ε₂ shock.',
    ],
  },
  2: {
    title: 'Positions Unwind',
    content: [
      'LT2 arrives with an offsetting demand of i units. With balanced order flow, the price S₂ equals the efficient price μ + ε₂.',
      'All inventory is unwound: MMs and LT1 sell to LT2 at the efficient price.',
      'No one bears inventory risk from t=2 to t=3 because S₂ = E₂[S₃] (the efficient price given current information).',
      'The market makers realize their expected profit (or loss) from providing liquidity at t=1.',
    ],
  },
  3: {
    title: 'Terminal Value Realized',
    content: [
      'The terminal value S₃ = μ + ε₂ + ε₃ is realized.',
      'Since all agents closed their positions at t=2, this value is purely informational - no trades occur.',
      'LT2, who bought at S₂, experiences the final shock ε₃ which determines whether they profit or lose.',
      'The model demonstrates how the demand for immediacy creates a systematic liquidity premium.',
    ],
  },
};

export function ExplanationPanel() {
  const { simulation } = useSimulationStore();

  if (!simulation) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Understanding the Model</h3>
        <div className="prose prose-sm text-gray-600">
          <p>
            The Grossman-Miller model explains how market liquidity is determined by the
            demand and supply of <strong>immediacy</strong>.
          </p>
          <p className="mt-2">
            When buyers and sellers arrive asynchronously, someone must warehouse inventory
            between their arrivals. Market makers provide this service, but demand
            compensation for bearing price risk.
          </p>
          <p className="mt-2">
            Run a simulation to see how this works step by step.
          </p>
        </div>
      </div>
    );
  }

  const { currentTime, params, equilibrium } = simulation;
  const explanation = EXPLANATIONS[currentTime];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-blue-800 mb-4">
        {explanation.title}
      </h3>

      <div className="space-y-3">
        {explanation.content.map((paragraph, idx) => (
          <p key={idx} className="text-sm text-gray-700 leading-relaxed">
            {paragraph}
          </p>
        ))}
      </div>

      {/* Dynamic Insights Based on Parameters */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          Current Scenario Insights
        </h4>

        <div className="space-y-2 text-sm text-gray-600">
          {currentTime === 1 && (
            <>
              <p>
                • With <strong>n = {params.n}</strong> market makers, each absorbs{' '}
                <strong>{(params.i / (params.n + 1)).toFixed(2)}</strong> units of the{' '}
                <strong>{params.i}</strong> unit imbalance.
              </p>
              <p>
                • The liquidity discount of{' '}
                <strong>{equilibrium.liquidityDiscount.toFixed(4)}</strong> represents{' '}
                <strong>
                  {((equilibrium.liquidityDiscount / params.mu) * 100).toFixed(2)}%
                </strong>{' '}
                of the fundamental value.
              </p>
              <p>
                • Immediacy is <strong>{(equilibrium.immediacy * 100).toFixed(1)}%</strong>,
                meaning LT1 completes {(equilibrium.immediacy * 100).toFixed(1)}% of their
                desired trade at t=1.
              </p>
            </>
          )}

          {currentTime === 2 && (
            <>
              <p>
                • The shock ε₂ = <strong>{simulation.shocks.epsilon2.toFixed(4)}</strong>{' '}
                moved the efficient price from μ = {params.mu} to S₂ ={' '}
                <strong>{equilibrium.S2.toFixed(4)}</strong>.
              </p>
              <p>
                • Market makers {simulation.shocks.epsilon2 > 0 ? 'benefited' : 'lost'} from
                holding inventory since S₂ {simulation.shocks.epsilon2 > 0 ? '>' : '<'} S₁.
              </p>
              <p>
                • LT2 absorbs the full imbalance of <strong>{params.i}</strong> units at
                the efficient price.
              </p>
            </>
          )}

          {currentTime === 3 && (
            <>
              <p>
                • The terminal shock ε₃ = <strong>{simulation.shocks.epsilon3.toFixed(4)}</strong>{' '}
                determined the final value S₃ = <strong>{equilibrium.S3.toFixed(4)}</strong>.
              </p>
              <p>
                • Total price movement from μ: {' '}
                <strong>
                  {(equilibrium.S3 - params.mu).toFixed(4)}
                </strong>{' '}
                (= ε₂ + ε₃).
              </p>
            </>
          )}
        </div>
      </div>

      {/* Key Economic Insight */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="text-sm font-medium text-blue-800">Key Insight</div>
        <p className="text-sm text-blue-700 mt-1">
          {currentTime === 1 &&
            'The liquidity discount compensates for risk: higher volatility (σ), risk aversion (a), or imbalance (i) increases the discount.'}
          {currentTime === 2 &&
            'With balanced order flow (LT2 arriving), the price returns to the efficient level - no compensation for risk is needed.'}
          {currentTime === 3 &&
            'The model shows why immediacy has value: LT1 traded below fair value, but avoided the uncertainty of waiting.'}
        </p>
      </div>
    </div>
  );
}
